import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CheckCircle,
  Email,
  Send,
  UnsubscribeOutlined,
  LocationOn,
} from '@mui/icons-material';
import axios from 'axios';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import TabDescription from './TabDescription';
import 'leaflet/dist/leaflet.css';
import { useRegionContext } from '../../region/RegionContext';

// ── Helpers ─────────────────────────────────────────────────────────────────

const getColor = (count, maxCount) => {
  const intensity = maxCount > 0 ? count / maxCount : 0;
  if (intensity > 0.7) return '#1565C0';
  if (intensity > 0.5) return '#1976D2';
  if (intensity > 0.3) return '#2196F3';
  if (intensity > 0.1) return '#64B5F6';
  return '#BBDEFB';
};

const DEFAULT_CENTER = [-25.2744, 133.7751]; // Australia
const DEFAULT_ZOOM = 4;

/**
 * Builds suburb/state-level clusters for the map from an array of venue docs.
 * Each venue should have bizLat/bizLon or fall back to state centres.
 */
const buildRegionData = (venues) => {
  const STATE_CENTRES = {
    VIC: { lat: -37.4713, lon: 144.7852 },
    NSW: { lat: -33.8688, lon: 151.2093 },
    QLD: { lat: -27.4698, lon: 153.0251 },
    SA: { lat: -34.9285, lon: 138.6007 },
    WA: { lat: -31.9505, lon: 115.8605 },
    TAS: { lat: -42.8821, lon: 147.3272 },
    ACT: { lat: -35.2809, lon: 149.13 },
    NT: { lat: -12.4634, lon: 130.8456 },
  };

  const counts = {};
  const coords = {};

  venues.forEach((v) => {
    const region = v.bizSuburb || v.bizState || 'Unknown';
    const lat = v.bizLat || v.latitude;
    const lon = v.bizLon || v.longitude;

    if (!counts[region]) {
      counts[region] = 0;
      coords[region] = { lats: [], lons: [] };
    }
    counts[region]++;

    if (lat && lon) {
      coords[region].lats.push(lat);
      coords[region].lons.push(lon);
    } else {
      const state = v.bizState?.toUpperCase();
      const centre = STATE_CENTRES[state] || STATE_CENTRES.VIC;
      coords[region].lats.push(centre.lat);
      coords[region].lons.push(centre.lon);
    }
  });

  return Object.entries(counts)
    .map(([name, count]) => {
      const c = coords[name];
      const avgLat = c.lats.reduce((a, b) => a + b, 0) / c.lats.length;
      const avgLon = c.lons.reduce((a, b) => a + b, 0) / c.lons.length;
      return { name, count, lat: avgLat, lon: avgLon };
    })
    .sort((a, b) => b.count - a.count);
};

// ── Sub-components ───────────────────────────────────────────────────────────

const VENUE_PAGE_SIZE = 25;

// Firestore Timestamp | {seconds} | Date | ISO string → epoch ms (or null)
export const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Newest claim first; venues without claimedAt last, by name. */
export const sortByClaimedAtDesc = (venues) =>
  [...venues].sort((a, b) => {
    const ta = toMillis(a.claimedAt);
    const tb = toMillis(b.claimedAt);
    if (ta !== null && tb !== null) return tb - ta;
    if (ta !== null) return -1;
    if (tb !== null) return 1;
    return (a.bizName || '').localeCompare(b.bizName || '');
  });

export const formatClaimedAt = (v) => {
  const ms = toMillis(v);
  return ms === null
    ? '—'
    : new Date(ms).toLocaleString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
      });
};

// dateColumn (optional): { label, field } — e.g. { label: 'Claimed', field: 'claimedAt' }
const VenueListTable = ({ venues, emptyMessage, page, setPage, dateColumn }) => (
  <>
  {venues.length > VENUE_PAGE_SIZE && (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
      <Button variant="outlined" size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
        Previous
      </Button>
      <Typography variant="body2">
        Page {page + 1} of {Math.ceil(venues.length / VENUE_PAGE_SIZE)} ({venues.length} total)
      </Typography>
      <Button variant="outlined" size="small" disabled={(page + 1) * VENUE_PAGE_SIZE >= venues.length} onClick={() => setPage(p => p + 1)}>
        Next
      </Button>
    </Box>
  )}
  <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400, overflowY: 'auto' }}>
    <Table size="small" stickyHeader aria-label="Venue claims list">
      <TableHead>
        <TableRow>
          <TableCell scope="col"><strong>Venue Name</strong></TableCell>
          <TableCell scope="col"><strong>Suburb</strong></TableCell>
          <TableCell scope="col"><strong>State</strong></TableCell>
          {dateColumn && <TableCell scope="col"><strong>{dateColumn.label}</strong></TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {venues.length === 0 ? (
          <TableRow>
            <TableCell colSpan={dateColumn ? 4 : 3} align="center">
              <Typography variant="body2" color="text.secondary">
                {emptyMessage}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          venues.slice(page * VENUE_PAGE_SIZE, (page + 1) * VENUE_PAGE_SIZE).map((v) => (
            <TableRow key={v.id} hover>
              <TableCell>{v.bizName || '(unnamed)'}</TableCell>
              <TableCell>{v.bizSuburb || '—'}</TableCell>
              <TableCell>{v.bizState || '—'}</TableCell>
              {dateColumn && <TableCell>{formatClaimedAt(v[dateColumn.field])}</TableCell>}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
  </>
);

const RegionMap = ({ venues, dotColor }) => {
  const regionData = buildRegionData(venues);
  const maxCount = regionData.length > 0 ? Math.max(...regionData.map((r) => r.count)) : 1;

  return (
    <Box sx={{ height: 480, borderRadius: 1, overflow: 'hidden', mt: 2 }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {regionData.map((r) => (
          <CircleMarker
            key={r.name}
            center={[r.lat, r.lon]}
            radius={Math.max(6, Math.sqrt(r.count) * 4)}
            pathOptions={{
              color: dotColor,
              fillColor: dotColor,
              fillOpacity: 0.65,
              weight: 2,
            }}
          >
            <Tooltip>
              <strong>{r.name}</strong>
              <br />
              {r.count} venue{r.count !== 1 ? 's' : ''}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </Box>
  );
};

const AU_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];

const SendEmailsPanel = () => {
  const { endpoints } = useRegionContext();
  const [batchSize, setBatchSize] = useState(100);
  const [stateFilter, setStateFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    setError(null);
    try {
      const body = { limit: batchSize };
      if (stateFilter) body.state = stateFilter;
      const { data } = await axios.post(endpoints.notifyUnclaimedVenues, body);
      setResult(data.results);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send batch. Check function logs.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sends "Claim Your Venue" emails to unclaimed venues that have a scraped email address
        but haven't been notified yet. Each email address is verified via Hunter.io before
        sending. Venues that have opted out are automatically skipped.
      </Typography>

      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Batch size</InputLabel>
            <Select
              value={batchSize}
              label="Batch size"
              onChange={(e) => setBatchSize(e.target.value)}
            >
              {[50, 100, 200, 500].map((n) => (
                <MenuItem key={n} value={n}>{n} venues</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>State filter</InputLabel>
            <Select
              value={stateFilter}
              label="State filter"
              onChange={(e) => setStateFilter(e.target.value)}
              inputProps={{ 'data-testid': 'state-filter-input' }}
            >
              <MenuItem value="">All states</MenuItem>
              {AU_STATES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            size="medium"
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
            onClick={handleSend}
            disabled={sending}
            fullWidth
          >
            {sending ? 'Sending…' : 'Send Batch'}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {result && (
        <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Last batch result
            </Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Venues in batch', value: result.total, color: 'text.primary' },
                { label: 'Emailed', value: result.emailed, color: 'success.main' },
                { label: 'Skipped (no valid email)', value: result.skipped, color: 'warning.main' },
                { label: 'Errors', value: result.errors, color: 'error.main' },
              ].map(({ label, value, color }) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {result.emailed < result.total && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {result.total - result.emailed - result.errors} venue(s) had no valid email
                address after Hunter.io verification. Run another batch to continue.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {!result && !error && (
        <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No batch sent yet this session. Each call picks the next{' '}
            <strong>{batchSize}</strong> unclaimed venues not yet notified.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * ClaimsEngagementTab
 *
 * Displays two panels (switchable via inner tabs):
 *   1. Claimed venues — venues where claimStatus === 'claimed'
 *   2. Unsubscribed venues — venues where emailOptOut === true
 *
 * Each panel shows a summary count card, a venue list, and a Leaflet map
 * with circle markers sized/coloured by regional density. The maps support
 * full zoom and pan so admins can drill into specific countries/regions.
 */
const ClaimsEngagementTab = () => {
  const { db, endpoints } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimedVenues, setClaimedVenues] = useState([]);
  const [optOutVenues, setOptOutVenues] = useState([]);
  const [innerTab, setInnerTab] = useState(0);
  const [claimedPage, setClaimedPage] = useState(0);
  const [optOutPage, setOptOutPage] = useState(0);

  useEffect(() => {
    if (db) loadData();
  }, [db]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [claimedSnap, optOutSnap] = await Promise.all([
        db.collection('Business').where('claimStatus', '==', 'claimed').limit(500).get(),
        db.collection('Business').where('emailOptOut', '==', true).limit(500).get(),
      ]);

      // Newest claims first (the query itself is unordered)
      setClaimedVenues(
        sortByClaimedAtDesc(claimedSnap.docs.map((d) => ({ id: d.id, ...d.data() })))
      );
      setOptOutVenues(
        optOutSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error('Error loading claims/engagement data:', err);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading claims & engagement data…</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        Tracks venue owner engagement with the claim process. Shows actively claimed venues (claimStatus:
        claimed) and venues that have opted out of email communications (emailOptOut: true) from the Business
        collection. Use this to monitor claim conversion rates by region, identify areas with low engagement,
        and manage email outreach campaigns to unclaimed venues.
      </TabDescription>
      {/* Summary row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              border: innerTab === 0 ? 2 : 1,
              borderColor: innerTab === 0 ? 'primary.main' : 'grey.200',
            }}
            onClick={() => setInnerTab(0)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 44, color: 'success.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Claimed Venues
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {claimedVenues.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Venues with an active claim
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              border: innerTab === 1 ? 2 : 1,
              borderColor: innerTab === 1 ? 'primary.main' : 'grey.200',
            }}
            onClick={() => setInnerTab(1)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <UnsubscribeOutlined sx={{ fontSize: 44, color: 'warning.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Unsubscribed Venues
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {optOutVenues.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Venues that opted out of emails
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              cursor: 'pointer',
              border: innerTab === 2 ? 2 : 1,
              borderColor: innerTab === 2 ? 'primary.main' : 'grey.200',
            }}
            onClick={() => setInnerTab(2)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Email sx={{ fontSize: 44, color: 'primary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Send Claim Emails
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    →
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Batch outreach to unclaimed venues
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Inner tabs */}
      <Card>
        <Tabs
          value={innerTab}
          onChange={(_, v) => setInnerTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<CheckCircle sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Claimed (${claimedVenues.length})`}
          />
          <Tab
            icon={<UnsubscribeOutlined sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={`Unsubscribed (${optOutVenues.length})`}
          />
          <Tab
            icon={<Send sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Send Emails"
          />
        </Tabs>

        <CardContent>
          {/* ── Claimed venues panel ── */}
          {innerTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Claimed venues map
                </Typography>
                <Chip
                  label={`${claimedVenues.length} total`}
                  color="success"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Circle size and colour intensity reflect the number of claimed venues per area.
                Zoom in for suburb-level detail.
              </Typography>

              <RegionMap venues={claimedVenues} dotColor="#2E7D32" />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                Full venue list
              </Typography>
              <VenueListTable
                venues={claimedVenues}
                emptyMessage="No claimed venues found."
                page={claimedPage}
                setPage={setClaimedPage}
                dateColumn={{ label: 'Claimed', field: 'claimedAt' }}
              />
            </Box>
          )}

          {/* ── Unsubscribed venues panel ── */}
          {innerTab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn color="warning" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Unsubscribed venues map
                </Typography>
                <Chip
                  label={`${optOutVenues.length} total`}
                  color="warning"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Shows venues whose owners have opted out of email communications.
                Dense clusters may indicate areas worth investigating.
              </Typography>

              <RegionMap venues={optOutVenues} dotColor="#E65100" />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                Full venue list
              </Typography>
              <VenueListTable
                venues={optOutVenues}
                emptyMessage="No unsubscribed venues found."
                page={optOutPage}
                setPage={setOptOutPage}
              />
            </Box>
          )}
          {/* ── Send emails panel ── */}
          {innerTab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Email color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Send claim emails
                </Typography>
              </Box>
              <SendEmailsPanel />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClaimsEngagementTab;
